import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  ALL_VALUE,
  Avatar,
  CenterSpinner,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  Png,
  SelectSheet,
  StatCard,
  SummaryCard,
  buildMonthOptions,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { resolveImageUrl } from "../../../utils/format";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, filterJobPosition } from "../../../utils/managerRole";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../../utils/useFilterOptions";
import { EVALUATION_STATUS_LABELS } from "../../evaluation/evaluationConstants";
import { getEmployeeKpiListApi } from "../../../api/statisticalReport.api";
import { EMP_API_BASE_URL, EMP_BASE_PATH } from "../../../config";

const LIMIT = 20;

const STATUS_OPTIONS = [
  { key: ALL_VALUE, label: "Tất cả" },
  ...Object.keys(EVALUATION_STATUS_LABELS).map((k) => ({ key: k, label: EVALUATION_STATUS_LABELS[k] })),
];
const SORT_OPTIONS = [
  { key: "NEWEST", label: "Mới đến cũ" },
  { key: "OLDEST", label: "Cũ đến mới" },
];

// Khớp statusUi/iconUi của StatisticalReportListCard.
const classificationUi = (code, consecutive) => {
  if (code === "XUAT_SAC") return { bg: "#DDEDFF", color: "#1277EB", icon: appImages.icon_award };
  if (code === "DAT" && Number(consecutive || 0) >= 2)
    return { bg: "#FFF1F0", color: "#E5484D", icon: appImages.icon_trending_up };
  if (code === "DAT") return { bg: "#EDFDF5", color: "#2ABA82", icon: appImages.icon_trending_up };
  if (code === "CANH_BAO_DO") return { bg: "#FFF1F0", color: "#E5484D", icon: appImages.icon_triangle_alert };
  if (code === "CAN_HO_TRO") return { bg: "#F5F3FF", color: "#8B5CF6", icon: appImages.icon_heart_handshake };
  return { bg: "#FFF7E8", color: "#F79D0D", icon: appImages.icon_triangle_alert };
};

const MetricBox = ({ icon, value, target, label, activeColor, activeBg }) => {
  const active = Number(value) >= Number(target);
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="8px"
      p="12px"
      borderRadius="20px"
      border="1px solid"
      borderColor={active ? activeColor : "#F7F9FC"}
      bg={active ? activeBg : "#F7F9FC"}
      w="31%"
    >
      <Png src={icon} size={16} color={active ? activeColor : M.grey200} />
      <Flex align="flex-end">
        <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
          {value}
        </Text>
        <Text fontSize="12px" fontWeight={500} color={M.grey200}>
          /{target}
        </Text>
      </Flex>
      <Text fontSize="12px" fontWeight={600} color={active ? activeColor : M.grey200} whiteSpace="nowrap">
        {label}
      </Text>
    </Flex>
  );
};

const KpiCard = ({ item, criteria, onClick }) => {
  const cls = item?.classification || {};
  const consecutive = Number(item?.metrics?.consecutiveMonthsWithoutTransactions || 0);
  const ui = classificationUi(cls?.code, consecutive);
  const label = cls?.code === "DAT" && consecutive >= 2 ? `${cls?.label || "-"} (*)` : cls?.label || "-";
  const m = item?.metrics || {};

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      onClick={onClick}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Flex align="flex-start" gap="16px">
        <Box position="relative" w="72px" h="72px" flexShrink={0}>
          <Avatar src={resolveImageUrl(item?.employee?.avatar, EMP_API_BASE_URL)} size={72} radius="8px" />
          <Flex
            position="absolute"
            bottom="-8px"
            right="-8px"
            w="32px"
            h="32px"
            borderRadius="8px"
            bg="white"
            align="center"
            justify="center"
          >
            <Flex w="28px" h="28px" borderRadius="8px" bg={ui.bg} align="center" justify="center">
              <Png src={ui.icon} size={20} color={ui.color} />
            </Flex>
          </Flex>
        </Box>

        <Box flex="1" minW="0">
          <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
            {item?.employee?.fullName || ""} {item?.employee?.fullName ? "-" : ""} {item?.employee?.code || ""}
          </Text>
          <Box h="8px" />
          <Flex align="center" gap="8px">
            <Box bg={ui.bg} borderRadius="6px" px="8px" py="3px">
              <Text fontSize="12px" fontWeight={600} color={ui.color}>
                {label}
              </Text>
            </Box>
            <Box h="12px" borderRight="1px solid" borderColor={M.border} />
            <Text fontSize="12px" fontWeight={600} color={M.primary}>
              {item?.employee?.jobPosition?.name || "-"}
            </Text>
          </Flex>
          <Box h="8px" />
          <Flex direction="column" gap="8px">
            <Flex align="center" gap="4px">
              <Png src={appImages.icon_building_2} size={12} color={M.gray} />
              <Text fontSize="12px" fontWeight={600} color={M.gray}>
                Khối: {item?.employee?.block?.name || "-"}
              </Text>
            </Flex>
            <Flex align="center" gap="4px">
              <Png src={appImages.icon_user_check} size={12} color={M.grey200} />
              <Text fontSize="12px" fontWeight={600} fontStyle="italic" color={M.grey200}>
                Vùng: {item?.employee?.area?.name || "-"}
              </Text>
            </Flex>
            <Flex align="center" gap="4px">
              <Png src={appImages.icon_map_pin} size={12} color={M.grey200} />
              <Text fontSize="12px" color={M.grey200}>
                {item?.employee?.branch?.name || "-"}
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Flex>

      <Box h="12px" />

      <Flex align="center" justify="space-between" gap="8px">
        <MetricBox
          icon={appImages.icon_phone_call}
          value={Number(m.consultCount || 0)}
          target={Number(criteria?.consultationTarget || 0)}
          label="TƯ VẤN"
          activeColor="#2F80ED"
          activeBg="#EFF6FF"
        />
        <MetricBox
          icon={appImages.icon_user_plus}
          value={Number(m.leadCount || 0)}
          target={Number(criteria?.minimumLeadTarget || 0)}
          label="LEAD CRM"
          activeColor="#2ABA82"
          activeBg="#EDFDF5"
        />
        <MetricBox
          icon={appImages.icon_circle_dollar_sign}
          value={Number(m.transactionCount || 0)}
          target={Number(criteria?.minimumTransactionTarget || 0)}
          label="GIAO DỊCH"
          activeColor="#F57127"
          activeBg="#FFF7ED"
        />
      </Flex>

      <Box h="12px" />

      <Flex align="center" gap="8px">
        <Box w="10px" h="10px" borderRadius="5px" bg={M.border} />
        <Flex align="flex-start" gap="8px">
          <Text fontSize="14px" fontWeight={600} fontStyle="italic" color={M.tertiary}>
            Tháng chưa chốt:
          </Text>
          <Text fontSize="14px" fontWeight={600} fontStyle="italic" color={M.baseContent}>
            {consecutive}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

const EmployeeKpiList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { user } = useEmployeeAuth();
  const jobPosition = filterJobPosition(user);
  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  const [filterOpen, setFilterOpen] = useState(false);
  const [monthValue, setMonthValue] = useState(() => moment().format("MM-YYYY"));
  const [status, setStatus] = useState(ALL_VALUE);
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const buildParams = useCallback(
    (p) => {
      const [month, year] = monthValue.split("-");
      return {
        page: p,
        limit: LIMIT,
        month: Number(month),
        year: Number(year),
        status: status !== ALL_VALUE ? status : undefined,
        areaId: validId(area),
        blockId: validId(block),
        branchId: validId(branch),
      };
    },
    [monthValue, status, area, block, branch]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getEmployeeKpiListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        if (data?.criteria) setCriteria(data.criteria);
        if (data?.summary) setSummary(data.summary);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthValue, status, area?._id, block?._id, branch?._id, sortKey]);

  const monthLabel = monthOptions.find((m) => m.value === monthValue)?.label || monthValue;
  const statusLabel = STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";
  const sortLabel = SORT_OPTIONS.find((s) => s.key === sortKey)?.label ?? "Mới đến cũ";

  // Lưới bộ lọc theo chức vụ - khớp renderFilterGrid của app.
  const filterFields = useMemo(() => {
    const f = {
      status: <FilterField key="status" label="Trạng thái" value={statusLabel} onClick={() => sheet.open("STATUS")} />,
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      month: <FilterField key="month" label="Tháng" value={monthLabel} onClick={() => sheet.open("MONTH")} />,
    };
    switch (jobPosition) {
      case JOB_TITLE.NHAN_VIEN:
        return [f.status, f.month];
      case JOB_TITLE.TRUONG_KHOI:
      case JOB_TITLE.TRUONG_VUNG:
        return [f.status, f.branch, f.block, f.month];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.status, f.branch, f.month];
      case JOB_TITLE.TGD:
        return [f.status, f.branch, f.area, f.block, f.month];
      default:
        return [f.status, f.branch, f.month];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, statusLabel, branch, area, block, monthLabel]);

  const cls = summary?.classifications || {};

  const sheetTitle =
    { STATUS: "Trạng thái", MONTH: "Chọn tháng", AREA: "Vùng", BLOCK: "Khối", BRANCH: "Chi nhánh", SORT: "Sắp xếp" }[
      sheet.type
    ] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "MONTH") return monthOptions.map((x) => ({ key: x.value, label: x.label, raw: x.value }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, area, block, branch, monthOptions]);

  const selectedKey = {
    STATUS: status,
    SORT: sortKey,
    MONTH: monthValue,
    AREA: area?._id,
    BLOCK: block?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "MONTH") setMonthValue(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["AREA", "BLOCK", "BRANCH"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const openDetail = (row) => {
      const [month, year] = monthValue.split("-");
      navigate(
        `${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien/${row.code}?month=${Number(
          month,
        )}&year=${Number(year)}`,
      );
    };

    const rows = items.map((it, i) => {
      const clsItem = it?.classification || {};
      const consecutive = Number(it?.metrics?.consecutiveMonthsWithoutTransactions || 0);
      const ui = classificationUi(clsItem?.code, consecutive);
      const m = it?.metrics || {};
      return {
        id: it.employee?.code || it._id || i,
        code: it.employee?.code,
        ui,
        avatar: resolveImageUrl(it?.employee?.avatar, EMP_API_BASE_URL),
        employee: `${it?.employee?.fullName || "-"} - ${it?.employee?.code || "-"}`,
        classLabel:
          clsItem?.code === "DAT" && consecutive >= 2
            ? `${clsItem?.label || "-"} (*)`
            : clsItem?.label || "-",
        jobPosition: it?.employee?.jobPosition?.name || "-",
        block: it?.employee?.block?.name || "-",
        area: it?.employee?.area?.name || "-",
        branch: it?.employee?.branch?.name || "-",
        consult: `${Number(m.consultCount || 0)}/${Number(criteria?.consultationTarget || 0)}`,
        lead: `${Number(m.leadCount || 0)}/${Number(criteria?.minimumLeadTarget || 0)}`,
        transaction: `${Number(m.transactionCount || 0)}/${Number(
          criteria?.minimumTransactionTarget || 0,
        )}`,
        consecutive: String(consecutive),
      };
    });

    return (
      <DesktopPage title="KPI nhân viên khối kinh doanh">
        <DesktopStatRow
          items={[
            { key: "total", title: "Tổng số nhân viên", total: summary?.totalSales ?? 0 },
            { key: "xs", title: "Nhân viên xuất sắc", total: cls.XUAT_SAC ?? 0, color: "#2F80ED" },
            { key: "dat", title: "Nhân viên đạt KPI", total: cls.DAT ?? 0, color: "#2ABA82" },
            { key: "cb", title: "Cần lưu ý", total: cls.CANH_BAO ?? 0, color: "#F57127" },
            { key: "cht", title: "Cần đào tạo thêm", total: cls.CAN_HO_TRO ?? 0, color: "#8B5CF6" },
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortLabel}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            onRowClick={openDetail}
            rows={rows}
            headers={[
              {
                key: "employee",
                label: "Nhân viên",
                align: "left",
                width: "22%",
                render: (row) => (
                  <Flex align="center" gap="12px">
                    <Avatar src={row.avatar} size={36} radius="8px" />
                    <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                      {row.employee}
                    </Text>
                  </Flex>
                ),
              },
              {
                key: "classLabel",
                label: "Xếp loại",
                width: "12%",
                render: (row) => (
                  <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="8px" py="3px">
                    <Text fontSize="13px" fontWeight={600} color={row.ui.color}>
                      {row.classLabel}
                    </Text>
                  </Box>
                ),
              },
              { key: "jobPosition", label: "Chức vụ", width: "10%" },
              { key: "block", label: "Khối", align: "left", width: "10%" },
              { key: "area", label: "Vùng", align: "left", width: "10%" },
              { key: "branch", label: "Chi nhánh", align: "left", width: "10%" },
              { key: "consult", label: "Tư vấn", width: "7%" },
              { key: "lead", label: "Lead CRM", width: "7%" },
              { key: "transaction", label: "Giao dịch", width: "7%" },
              { key: "consecutive", label: "Tháng chưa chốt", width: "5%" },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="KPI nhân viên khối kinh doanh" />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text="TỔNG SỐ NHÂN VIÊN" total={summary?.totalSales ?? 0} />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          <StatCard
            subTitle="XUẤT SẮC"
            title="Nhân viên xuất sắc"
            value={cls.XUAT_SAC ?? 0}
            icon={appImages.icon_award}
            bgColor="#EFF6FF"
            textColor="#2F80ED"
          />
          <StatCard
            subTitle="ĐẠT KPI"
            title="Nhân viên đạt KPI"
            value={cls.DAT ?? 0}
            icon={appImages.icon_trending_up}
            bgColor="#EDFDF5"
            textColor="#2ABA82"
          />
          <StatCard
            subTitle="CẢNH BÁO"
            title="Cần lưu ý"
            value={cls.CANH_BAO ?? 0}
            icon={appImages.icon_triangle_alert}
            bgColor="#FFF7ED"
            textColor="#F57127"
          />
          <StatCard
            subTitle="CẦN HỖ TRỢ"
            title="Cần đào tạo thêm"
            value={cls.CAN_HO_TRO ?? 0}
            icon={appImages.icon_heart_handshake}
            bgColor="#F5F3FF"
            textColor="#8B5CF6"
          />
        </Flex>

        <ListHeaderRow title="Danh sách nhân viên" sortLabel={sortLabel} onSort={() => sheet.open("SORT")} mb="12px" />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it, i) => (
              <KpiCard
                key={it.employee?.code || it._id || i}
                item={it}
                criteria={criteria}
                onClick={() => {
                  const [month, year] = monthValue.split("-");
                  navigate(
                    `${EMP_BASE_PATH}/quan-ly/bao-cao/kpi-nhan-vien/${it.employee?.code}?month=${Number(
                      month
                    )}&year=${Number(year)}`
                  );
                }}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default EmployeeKpiList;
